import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { MeilisearchService } from '@app/core/services/meilisearch.service';
import { SharedLibModule } from '@app/core/shared-lib/shared-lib.module';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { debounceTime, switchMap } from 'rxjs';
import { SearchModalHitComponent } from './search-modal-hit.component';

@Component({
  selector: 'search-modal',
  template: `
    <form [formGroup]="form" class="flex flex-col gap-2">
      <input formControlName="searchQuery" placeholder="Cerca" pInputText class="w-full" pAutoFocus [autofocus]="true" />
      <p-selectButton [options]="indexes" formControlName="selectedIndexes" [multiple]="true" optionLabel="label" optionValue="value" />
    </form>
    <div class="flex flex-col gap-2 mt-4">
      @for (item of indexes; track $index) { @if (item.value === 'vendita' && isIndexSelected('vendita')) {
      @if (getResults(item.value).length > 0) {
      @for (result of getResults(item.value); track $index) {
      <app-search-modal-hit
        [data]="result"
        [routerLink]="['/sales/working-unit/info']"
        [queryParams]="{ id: result._id }"
        [indexType]="item.label"
        [indexSeverity]="'success'"
        (click)="dialogRef.close()"
      ></app-search-modal-hit>
      } } } @if (item.value === 'acquisto' && isIndexSelected('acquisto')) {
      @if (getResults(item.value).length > 0) {
      @for (result of getResults(item.value); track $index) {
      <app-search-modal-hit
        [data]="result"
        [routerLink]="['/purchases/working-unit/info']"
        [queryParams]="{ id: result._id }"
        [indexType]="item.label"
        [indexSeverity]="'info'"
        (click)="dialogRef.close()"
      ></app-search-modal-hit>

      } } } @if (item.value === 'vendita_rentings' && isIndexSelected('vendita_rentings')) {
      @if (getResults(item.value).length > 0) {
      @for (result of getResults(item.value); track $index) {
      <app-search-modal-hit
        [data]="result"
        [routerLink]="['/sales/working-unit/info']"
        [queryParams]="{ id: result._id }"
        [indexType]="item.label"
        [indexSeverity]="'warning'"
        (click)="dialogRef.close()"
      ></app-search-modal-hit>
      } } } @if (item.value === 'acquisto_rentings' && isIndexSelected('acquisto_rentings')) {
      @if (getResults(item.value).length > 0) {
      @for (result of getResults(item.value); track $index) {
      <app-search-modal-hit
        [data]="result"
        [routerLink]="['/purchases/working-unit/info']"
        [queryParams]="{ id: result._id }"
        [indexType]="item.label"
        [indexSeverity]="'secondary'"
        (click)="dialogRef.close()"
      ></app-search-modal-hit>
      } } } @if (item.value === 'events' && isIndexSelected('events')) {
      @if (getResults(item.value).length > 0) {
      @for (result of getResults(item.value); track $index) {
      <app-search-modal-hit
        [data]="result"
        [routerLink]="[
          result.projectSale ? '/sales/working-unit/appointments' : result.projectPurchase ? '/purchases/working-unit/appointments' : ''
        ]"
        [queryParams]="{ id: result.projectSale ? result.projectSale : result.projectPurchase }"
        [indexType]="item.label"
        (click)="dialogRef.close()"
        [indexSeverity]="'danger'"
      ></app-search-modal-hit>
      } } } @if (item.value === 'contacts' && isIndexSelected('contacts')) {
      @if (getResults(item.value).length > 0) {
      @for (result of getResults(item.value); track $index) {
      <app-search-modal-hit
        [data]="result"
        [routerLink]="[
          result.type == 'individual' ? '/contacts/individual/upsert/' + result._id : '/contacts/company/upsert/' + result._id
        ]"
        [indexType]="item.label"
        [indexSeverity]="'contrast'"
        (click)="dialogRef.close()"
      ></app-search-modal-hit>
      } } } }
    </div>
  `,
  standalone: true,
  imports: [SharedLibModule, SearchModalHitComponent],
  providers: [MeilisearchService],
})
export class SearchModalComponent implements OnInit {
  dialogRef = inject(DynamicDialogRef);
  fb = inject(FormBuilder);
  form = this.fb.group({
    searchQuery: this.fb.nonNullable.control<string>(''),
    selectedIndexes: this.fb.nonNullable.control<string[]>([]),
  });
  meilisearchService = inject(MeilisearchService);
  destoryRef = inject(DestroyRef);
  indexes = [
    { label: 'Vendite', value: 'vendita' },
    { label: 'Acquisti', value: 'acquisto' },
    { label: 'Locazioni', value: 'vendita_rentings' },
    { label: 'Affitti', value: 'acquisto_rentings' },
    { label: 'Appuntamenti', value: 'events' },
    { label: 'Contatti', value: 'contacts' },
  ];

  results: Map<string, any[]> = new Map();

  getResults(index: string): any[] {
    return this.results.get(index) ?? [];
  }

  isIndexSelected(index: string): boolean {
    const selectedIndexes = this.form.get('selectedIndexes')?.value;
    return !selectedIndexes || selectedIndexes.length === 0 || selectedIndexes.includes(index);
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(300),
        switchMap((value) => {
          const selectedIndexes =
            value.selectedIndexes && value.selectedIndexes.length > 0 ? value.selectedIndexes : this.indexes.map((i) => i.value);
          return this.meilisearchService.search(value.searchQuery ?? '', selectedIndexes);
        }),
        takeUntilDestroyed(this.destoryRef),
      )
      .subscribe((value) => {
        this.results = value;
      });
  }
}
